import { useEffect, useState } from 'react';
import { IState } from '../../../redux/redux';
import Icon, { IconType } from '../../Icon/Icon';
import { ICircleInfo } from '../../_Core/CoreTypes';
import { store } from '../../../redux/oldStore';
import { ServerResponse } from '../../../utils/Server';
import { toast } from 'react-toastify';
import { IAssignmentInfo, ICirlceInfoApiCall, IProjectAssignment } from './JudgingCirlcesTypes';

interface IProps {
  state: IState;
  circle?: ICircleInfo;
  info: ICirlceInfoApiCall;
  onUpdate: (circle: ICircleInfo) => void;
  onRemove: (circle: ICircleInfo) => void;
}

const JudgingCircleManager = (props: IProps) => {
  const [circle, setCircle] = useState<ICircleInfo>({});
  const [assignmentInfo, setAssignmentInfo] = useState<IAssignmentInfo>();
  const [selectedProjectToAdd, setSelectedProjectToAdd] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<'settings' | 'entries'>('settings');

  useEffect(() => {
    setCircle(props.circle ?? {});
  }, [props.circle]);

  useEffect(() => {
    loadAssignmentInfo();
  }, []);

  const loadAssignmentInfo = () => {
    store.server.getApi<ServerResponse<IAssignmentInfo>>(`../JudgingCircles/AssignmentInfo`)
    .then((x) => {
      if (x.Success) {
        setAssignmentInfo(x.Value);
      } else {
        toast.error(x.Message);
      }
    });
  };

  const onSave = () => {
    toast.dismiss();
    let errors:string[] = [];
    if(!circle.name) errors.push(`You must set the name of this new circle.`);
    if((circle.roundId ?? 0) <= 0) errors.push(`You must select the round of this circle.`);

    if(errors.length > 0){
        toast.warning(<>Oops! Can't save yet.
        <ul>
            {errors.map((e,i)=>{
                return <li key={`jcm-e-s-${i}`}>{e}</li>
            })}
        </ul>
        </>)
        return;
    }

    store.server.postApi<ServerResponse<ICircleInfo>>(`../JudgingCircles/CircleUpdate`,circle)
    .then(x=>{
        if(x.Success){
            toast.success(`Circle "${circle.name}" was saved!`);            
            setCircle(x.Value);
            props.onUpdate(x.Value);
        } else {
            toast.error(x.Message, {autoClose:false});
        }
    });
  };

  const onRemove = () => {
    if(!window.confirm(`${props.state.User?.firstName} are you sure you want to remove cirlce "${circle.name}"?`)){
        return;
    }

    toast.dismiss();
    store.server.postApi<ServerResponse<boolean>>(`../JudgingCircles/CircleRemove`,{circleId:circle.id})
    .then((x)=>{
        if(x.Success){
            toast.info(`Circle "${circle.name}" was removed.`);
            props.onRemove(circle);
        } else {
            toast.error(x.Message, {autoClose:false});
        }
    });
  };

  const removeProjectAssignment = (assignment:IProjectAssignment) => {
    if((circle.roundId ?? 0) < 1){
      toast.warning(`Set a round before changing entry assignments.`);
      return;
    }

    store.server.postApi<ServerResponse<boolean>>(`../JudgingCircles/ClearProjectAssignment`, {
      roundId: circle.roundId,
      projectId: assignment.ProjectId,
    })
    .then((x) => {
      if (x.Success) {
        toast.success(`Entry ${assignment.ProjectId} removed from this circle.`);
        loadAssignmentInfo();
      } else {
        toast.error(x.Message);
      }
    });
  };

  const addProjectAssignment = () => {
    if((circle.id ?? 0) < 1){
      toast.warning(`Save the circle before adding entries.`);
      return;
    }
    if((circle.roundId ?? 0) < 1){
      toast.warning(`Set a round before adding entries.`);
      return;
    }
    if(!selectedProjectToAdd){
      toast.info(`Select an entry to add.`);
      return;
    }

    store.server.postApi<ServerResponse<boolean>>(`../JudgingCircles/ProjectAssignment`, {
      roundId: circle.roundId,
      projectId: +selectedProjectToAdd,
      circleId: circle.id,
    })
    .then((x) => {
      if (x.Success) {
        toast.success(`Entry added to this circle.`);
        setSelectedProjectToAdd('');
        loadAssignmentInfo();
      } else {
        toast.error(x.Message);
      }
    });
  };

  const circleAssignments = (assignmentInfo?.ProjectAssignments ?? []).filter((a) => {
    return a.CircleId === circle.id && a.RoundId === circle.roundId;
  });

  const availableProjects = (assignmentInfo?.Projects ?? []).filter((p) => {
    return !(assignmentInfo?.ProjectAssignments ?? []).some((a) => a.ProjectId === p.id && a.RoundId === circle.roundId);
  });

  return (
    <>
      <div className="form-horizontal">
        <ul className="nav nav-tabs" role="tablist">
          <li role="presentation" className={selectedTab === 'settings' ? 'active' : ''}>
            <a onClick={() => { setSelectedTab('settings'); }}>
              Circle Settings
            </a>
          </li>
          <li role="presentation" className={selectedTab === 'entries' ? 'active' : ''}>
            <a onClick={() => { setSelectedTab('entries'); }}>
              Entries
            </a>
          </li>
        </ul>

        <div className="headroom">
          {selectedTab === 'settings' && (
            <>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor='jcm-name'>Circle Name</label>
                <div className="col-sm-8">
                  <input
                      type={'text'}
                      id={'jcm-name'}
                      className='form-control'
                      maxLength={250}
                      value={circle.name ?? ''}
                      onChange={(x)=>{setCircle({...circle,name:x.target.value});}}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor='jcm-round'>Round</label>
                <div className="col-sm-8">
                  <select
                      id={'jcm-round'}
                      className='form-control'
                      value={circle.roundId ?? 0}
                      onChange={(x)=>{setCircle({...circle,roundId:+x.target.value});}}
                  >
                      <option value={0}>Select Round</option>
                      {
                          props.info.Rounds.map((x,i)=>{
                              return <option key={`kjcm-e-r-${i}`} value={x.id ?? 0}>{x.name}</option>
                          })
                      }
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor='jcm-category'>Category</label>
                <div className="col-sm-8">
                  <select
                      id={'jcm-category'}
                      className='form-control'
                      value={circle.categoryId ?? 0}
                      onChange={(x)=>{setCircle({...circle,categoryId:+x.target.value});}}
                  >
                      <option value={0}>Select category</option>
                      {
                          props.info.Categories.map((x,i)=>{
                              return <option key={`kjcm-e-c-${i}`} value={x.id ?? 0}>{x.name}</option>
                          })
                      }
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor='jcm-division'>Division</label>
                <div className="col-sm-8">
                  <select
                      id={'jcm-division'}
                      className='form-control'
                      value={circle.divisionId ?? 0}
                      onChange={(x)=>{setCircle({...circle,divisionId:+x.target.value});}}
                  >
                      <option value={0}>Select division</option>
                      {
                          props.info.Divisions.map((x,i)=>{
                              return <option key={`kjcm-e-di-${i}`} value={x.id ?? 0}>{x.name}</option>
                          })
                      }
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor='jcm-judgeGroup'>Judge Group</label>
                <div className="col-sm-8">
                  <select
                      id={'jcm-judgeGroup'}
                      className='form-control'
                      value={circle.judgeGroup ?? 0}
                      onChange={(x)=>{setCircle({...circle,judgeGroup:+x.target.value});}}
                  >
                      <option value={0}>Select judge group</option>
                      {
                          props.info.JudgeGroups.map((x,i)=>{
                              return <option key={`kjcm-e-jg-${i}`} value={x.id ?? 0}>{x.name}</option>
                          })
                      }
                  </select>
                </div>
              </div>
              <div className="form-group">
                <div className="col-sm-4"></div>
                <div className="col-sm-8">
                  <button type={'button'} className="btn btn-secondary" onClick={onSave}>
                    <Icon type={IconType.save} />
                    Save
                  </button>
                  {(circle.id ?? 0) > 0 && (
                    <button type={'button'} className="btn btn-default" onClick={onRemove}>
                      <Icon type={IconType.trashO} />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {selectedTab === 'entries' && (
            <>
              {(circle.id ?? 0) > 0 ? (
                <>
                  <div className="form-group">
                    <label className="col-sm-4 control-label">Assigned Entries</label>
                    <div className="col-sm-8">
                      {circleAssignments.length === 0 && <div className="small text-muted">No entries assigned to this circle.</div>}
                      {circleAssignments.map((a) => {
                        return (
                          <div className="flex-between" key={`jcm-project-assignment-${a.ProjectId}`}>
                            {a.ProjectId}
                            <button
                              type={'button'}
                              className="btn btn-xs btn-default text-danger"
                              title={`Remove project ${a.ProjectId}`}
                              onClick={() => { removeProjectAssignment(a); }}
                            >
                              <Icon type={IconType.trashO} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="col-sm-4 control-label" htmlFor="jcm-add-project">Add Entry</label>
                    <div className="col-sm-8">
                      <div className="input-group">
                        <select
                          id="jcm-add-project"
                          className="form-control"
                          value={selectedProjectToAdd}
                          onChange={(x) => {
                            setSelectedProjectToAdd(x.target.value);
                          }}
                        >
                          <option value={''}>Select entry</option>
                          {availableProjects.map((p) => {
                            return <option key={`jcm-add-project-${p.id}`} value={p.id}>{p.projectId} - {p.title}</option>
                          })}
                        </select>
                        <div className="input-group-btn">
                        <button
                          type={'button'}
                          className="btn btn-success bummper-left"
                          title="Add project to this circle"
                          onClick={addProjectAssignment}
                          disabled={!selectedProjectToAdd}
                        >
                          <Icon type={IconType.plus} />
                        </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="alert alert-info">
                  Save the circle first, then use this tab to add or remove entries.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default JudgingCircleManager;
